// ============================================================================
// RENDU HUMAIN D'UN NOM DE PERSONNE — satellite de présentation (SPEC §24.6bis).
//
// POURQUOI SÉPARÉ EN BASE, RECOMPOSÉ À L'AFFICHAGE. Un champ « nom complet » unique se paie à chaque
// usage : personnaliser un e-mail (« Bonjour Claire », pas « Bonjour Claire Bonnet »), trier par nom
// de famille, afficher des initiales. Chacun de ces usages devrait alors DÉCOUPER la chaîne — et un
// découpage est une devinette qui échoue sur « Jean-Pierre De La Tour ». Découper une fois à la SAISIE,
// où la personne elle-même distingue les deux, est la seule opération fiable ; recomposer est trivial.
//
// ⚠️ CE MODULE EST LE SEUL ENDROIT qui décide comment recomposer. Trois applications affichent des
// noms (annuaire d'identités, Console, back-office) : sans point unique, chacune inventerait sa règle
// de repli et deux écrans afficheraient deux choses pour la même personne.
//
// ⚠️ ZÉRO DÉPENDANCE, zéro `Intl` — comme `date-format` et `money-format` : le rendu doit être
// identique sur toutes les plateformes, Hermes (React Native) compris.
// ============================================================================

/** Une personne telle qu'elle est STOCKÉE : deux champs, chacun pouvant manquer. */
export interface PersonName {
  firstName?: string | null;
  lastName?: string | null;
}

const propre = (v: string | null | undefined): string => (v ?? '').trim().replace(/\s+/g, ' ');

/**
 * Nom d'affichage : « Claire Bonnet ».
 *
 * ⚠️ Le repli n'invente rien : s'il ne reste qu'un champ on l'affiche seul, et si les deux manquent on
 * rend `fallback` (typiquement l'e-mail) plutôt qu'une chaîne vide — une ligne sans libellé dans une
 * liste ne se distingue pas d'une ligne cassée.
 */
export function formatName(p: PersonName, fallback = ''): string {
  const prenom = propre(p.firstName);
  const nom = propre(p.lastName);
  return [prenom, nom].filter(Boolean).join(' ') || propre(fallback);
}

/**
 * Nom d'usage pour s'adresser à quelqu'un : le PRÉNOM (« Bonjour Claire, »).
 *
 * ⚠️ Repli sur le nom de famille, jamais sur l'e-mail : « Bonjour claire.bonnet@… » est pire que
 * « Bonjour, » tout court, que l'appelant obtient en recevant une chaîne vide.
 */
export function greetingName(p: PersonName): string {
  return propre(p.firstName) || propre(p.lastName);
}

/**
 * Prénom d'usage tiré d'un nom d'affichage STOCKÉ EN UN SEUL CHAMP (« Claire Bonnet » → « Claire »).
 *
 * ⚠️ POUR L'AFFICHAGE SEULEMENT, JAMAIS POUR STOCKER. C'est la différence avec `splitLegacyName`, qui
 * découpe pour ÉCRIRE et n'a donc sa place que dans une migration : ici rien n'est enregistré, et une
 * découpe inexacte ne coûte qu'un salut maladroit — pas une fiche fausse.
 * ⚠️ LA CONVENTION « PRÉNOM D'ABORD » VIENT DU FORMULAIRE, pas d'une supposition : le champ « Nom
 * complet » y est illustré par « Céline Vireau ». Un compte saisi à l'envers (« DUPONT Jean ») sera
 * donc salué « Bonjour DUPONT » — le jour où les comptes porteront deux champs séparés, cette
 * fonction disparaîtra au profit de `greetingName`.
 */
export function greetingFromDisplayName(complet: string | null | undefined): string {
  return propre(complet).split(' ').filter(Boolean)[0] ?? '';
}

/**
 * LES LETTRES ACCENTUÉES, RAMENÉES À LEUR BASE — la table que `searchNorm` applique.
 *
 * ⚠️ UNE TABLE ET NON `normalize('NFD')`, ET C'EST UNE CONTRAINTE DE MOTEUR. Ce module est lu par du
 * React Native, donc par Hermes, dont le support d'Unicode n'est pas celui de Node ; la caisse porte
 * déjà l'avertissement dans son propre normaliseur. Une recette qui marche au banc et rend la chaîne
 * inchangée sur le terminal donnerait une recherche vide en magasin, et verte en intégration.
 *
 * ⚠️ ELLE COUVRE EXACTEMENT CE QUE NFD PLIERAIT, et une garde le prouve caractère par caractère sur
 * tout le latin étendu — c'est elle qui empêche cette table de dériver de la référence.
 */
const FOLDED: Record<string, string> = {
  à: 'a', á: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a', ā: 'a', ă: 'a', ą: 'a',
  ç: 'c', ć: 'c', ĉ: 'c', ċ: 'c', č: 'c',
  ď: 'd',
  è: 'e', é: 'e', ê: 'e', ë: 'e', ē: 'e', ĕ: 'e', ė: 'e', ę: 'e', ě: 'e',
  ĝ: 'g', ğ: 'g', ġ: 'g', ģ: 'g',
  ĥ: 'h',
  ì: 'i', í: 'i', î: 'i', ï: 'i', ĩ: 'i', ī: 'i', ĭ: 'i', į: 'i',
  ĵ: 'j',
  ķ: 'k',
  ĺ: 'l', ļ: 'l', ľ: 'l',
  ñ: 'n', ń: 'n', ņ: 'n', ň: 'n',
  ò: 'o', ó: 'o', ô: 'o', õ: 'o', ö: 'o', ō: 'o', ŏ: 'o', ő: 'o',
  ŕ: 'r', ŗ: 'r', ř: 'r',
  ś: 's', ŝ: 's', ş: 's', š: 's',
  ţ: 't', ť: 't',
  ù: 'u', ú: 'u', û: 'u', ü: 'u', ũ: 'u', ū: 'u', ŭ: 'u', ů: 'u', ű: 'u', ų: 'u',
  ŵ: 'w',
  ý: 'y', ÿ: 'y', ŷ: 'y',
  ź: 'z', ż: 'z', ž: 'z',
};

/**
 * Le PLIAGE DES ACCENTS, seul et nu — la recette partagée entre ce qui ÉCRIT `name_norm` et ce qui
 * la CHERCHE.
 *
 * ⚠️ ELLE VIT ICI PARCE QUE LES DEUX BOUTS DOIVENT L'APPLIQUER À L'IDENTIQUE. Le back-office remplit
 * `article.name_norm` ; le back-office la cherche, et Mobile Partner la cherche aussi, sur une copie
 * descendue. Deux recettes voisines donneraient un écran qui ne trouve pas la ligne que l'autre
 * moitié a produite — sans erreur nulle part, juste un « aucun article ».
 *
 * ⚠️ ELLE NE DÉPLIE AUCUNE LIGATURE, à dessein : « œillet » reste « œillet ». La caisse, elle, plie
 * `œ` en `oe` (`retail/src/db/normalize.ts`) — c'est tenable parce qu'elle écrit ET lit sa propre
 * colonne, donc elle est d'accord avec elle-même. Les aligner exigerait de RÉÉCRIRE les colonnes
 * déjà remplies des deux côtés : une migration, pas un renommage.
 */
export function searchNorm(s: string | null | undefined): string {
  let out = '';
  // ⚠️ MINUSCULE D’ABORD : la table ne porte que des minuscules, la doubler en capitales donnerait
  //    deux endroits à tenir d’accord pour un seul fait.
  for (const c of (s ?? '').toLowerCase()) {
    // ⚠️ LES DIACRITIQUES DÉTACHÉS TOMBENT AUSSI (\u0300-\u036f), et ce n’est pas de la
    //    théorie : « İ » minusculé donne « i » SUIVI d’un point combinant, et une saisie collée
    //    depuis un traitement de texte arrive souvent décomposée. Sans cette ligne, ces deux cas
    //    traversent le pliage intacts et ne trouvent jamais la colonne.
    const code = c.charCodeAt(0);
    if (code >= 0x0300 && code <= 0x036f) continue;
    out += FOLDED[c] ?? c;
  }
  return out;
}

/**
 * Clé de tri par NOM DE FAMILLE puis prénom — l'ordre d'un annuaire.
 *
 * ⚠️ Accents pliés et casse retirée : sans cela « Élodie » se range après « Zoé » dans un tri binaire,
 * et l'utilisateur conclut que la liste n'est pas triée.
 */
export function sortKey(p: PersonName): string {
  // ⚠️ LE PLIAGE EST CELUI DE `searchNorm`, il n’est plus réécrit ici : trier et chercher doivent
  //    ranger « Élodie » au même endroit, sinon la ligne trouvée n’est pas celle qu’on voit.
  return `${searchNorm(propre(p.lastName))} ${searchNorm(propre(p.firstName))}`.trim();
}

/** Initiales pour une pastille : « CB ». Vide si on ne sait rien — jamais un caractère inventé. */
export function initials(p: PersonName, fallback = ''): string {
  const prenom = propre(p.firstName);
  const nom = propre(p.lastName);
  const lettres = (prenom[0] ?? '') + (nom[0] ?? '');
  return (lettres || propre(fallback)[0] || '').toUpperCase();
}

/**
 * Découpe un « nom complet » historique en prénom + nom.
 *
 * ⚠️ RÉSERVÉ À LA REPRISE DE L'EXISTANT — c'est précisément l'opération qu'on cesse de faire. Les
 * comptes créés avant la séparation ne portent qu'une chaîne, saisie sous un libellé « Nom complet »
 * dont l'exemple était « Céline Vireau » : la convention « prénom d'abord » est donc celle du
 * formulaire, pas une supposition. Le premier mot devient le prénom, le RESTE le nom (« Jean-Pierre
 * De La Tour » → « Jean-Pierre » / « De La Tour »), ce qui est juste dans ce sens et faux dans
 * l'autre. À n'appeler que depuis une migration, jamais dans du code d'exécution.
 */
export function splitLegacyName(complet: string | null | undefined): { firstName: string; lastName: string } {
  const mots = propre(complet).split(' ').filter(Boolean);
  if (mots.length === 0) return { firstName: '', lastName: '' };
  if (mots.length === 1) return { firstName: mots[0]!, lastName: '' };
  return { firstName: mots[0]!, lastName: mots.slice(1).join(' ') };
}
